import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Form, Input, Select, Spin } from '@arco-design/web-react';
import {
  api,
  errorMessage,
  isAmbiguousRequestFailure,
} from '../../../common/api/httpClient';
import type { PortalWorkItemListItem } from '../model/myTasks';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';

type Application = {
  applicationPublicId: string; tenantName: string; tenantTypePublicId: string;
  managerUsername: string; managerFullName: string; version: number;
};
type TenantType = { tenantTypePublicId: string; tenantTypeName: string };
type Values = {
  tenantName: string; tenantTypePublicId: string;
  managerUsername: string; managerFullName: string; managerInitialPassword?: string;
};

export type TenantApplicationEditHandle = {
  save: () => Promise<number | undefined>;
  prepareSubmit: () => Promise<number | undefined>;
};

type Props = {
  task?: PortalWorkItemListItem;
  draft?: {
    draftPublicId: string;
    businessObjectPublicId: string;
    expectedDraftVersion: number;
  };
  onDraftSaved?: (expectedDraftVersion: number) => void;
  onRuntimeStatusChange?: (taskId: string, ready: boolean) => void;
};

type DraftResult = {
  application: Application;
  draft: {
    expectedDraftVersion: number;
    receiptStatus: string;
  };
};

export const TenantApplicationEditPage = forwardRef<
TenantApplicationEditHandle, Props>(
function TenantApplicationEditPage(props, ref) {
  const { message } = usePortalOverlay();
  const [form] = Form.useForm<Values>();
  const [application, setApplication] = useState<Application>();
  const [types, setTypes] = useState<TenantType[]>([]);
  const [loading, setLoading] = useState(true);
  const [draftVersion, setDraftVersion] = useState(
    props.draft?.expectedDraftVersion,
  );
  const saveOperation = useRef<string | undefined>(undefined);

  const businessObjectPublicId = props.draft
    ?.businessObjectPublicId
    ?? props.task?.relatedObject.code;

  useEffect(() => {
    setDraftVersion(
      props.draft?.expectedDraftVersion);
  }, [props.draft?.expectedDraftVersion]);

  useEffect(() => {
    if (!businessObjectPublicId) return;
    let active = true;
    const taskId = props.task?.taskId ?? '';
    props.onRuntimeStatusChange?.(taskId, false);
    void Promise.all([
      api<Application>(
        `/api/admin/tenant-applications/${businessObjectPublicId}`),
      api<TenantType[]>('/api/admin/tenant-types?status=ENABLED'),
    ]).then(([current, tenantTypes]) => {
      if (!active) return;
      setApplication(current);
      setTypes(tenantTypes);
      form.setFieldsValue({
        tenantName: current.tenantName, tenantTypePublicId: current.tenantTypePublicId,
        managerUsername: current.managerUsername, managerFullName: current.managerFullName,
      });
      props.onRuntimeStatusChange?.(taskId, true);
    }).catch(cause => {
      props.onRuntimeStatusChange?.(taskId, false);
      message.error(errorMessage(cause));
    })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      props.onRuntimeStatusChange?.(taskId, false);
    };
  }, [businessObjectPublicId, form, message, props.onRuntimeStatusChange]);

  const save = async () => {
    if (!application) throw new Error('租户申请尚未加载完成');
    const values = await form.validate();
    let changed: Application;
    let nextDraftVersion: number | undefined;
    if (props.draft) {
      if (draftVersion === undefined) {
        throw new Error('草稿版本尚未加载完成');
      }
      const operationId = saveOperation.current
        ?? crypto.randomUUID();
      saveOperation.current = operationId;
      try {
        const result = await api<DraftResult>(
          `/api/admin/tenant-applications/drafts/${props.draft.draftPublicId}`,
          {
            method: 'PUT',
            body: JSON.stringify({
              ...values,
              version: application.version,
              expectedDraftVersion: draftVersion,
              operationId,
            }),
          },
        );
        saveOperation.current = undefined;
        changed = result.application;
        nextDraftVersion = result.draft.expectedDraftVersion;
        setDraftVersion(nextDraftVersion);
        props.onDraftSaved?.(nextDraftVersion);
      } catch (cause) {
        if (!isAmbiguousRequestFailure(cause)) {
          saveOperation.current = undefined;
        }
        throw cause;
      }
    } else {
      changed = await api<Application>(
        `/api/admin/tenant-applications/${application.applicationPublicId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            ...values,
            version: application.version,
          }),
        });
    }
    setApplication(changed);
    form.setFieldsValue({
      tenantName: changed.tenantName,
      tenantTypePublicId: changed.tenantTypePublicId,
      managerUsername: changed.managerUsername,
      managerFullName: changed.managerFullName,
      managerInitialPassword: undefined,
    });
    return nextDraftVersion;
  };

  const prepareSubmit = async () => {
    if (!application) {
      throw new Error('租户申请尚未加载完成');
    }
    const values = await form.validate();
    const dirty = values.tenantName?.trim()
      !== application.tenantName
      || values.tenantTypePublicId
      !== application.tenantTypePublicId
      || values.managerUsername?.trim()
      !== application.managerUsername
      || values.managerFullName?.trim()
      !== application.managerFullName
      || Boolean(values.managerInitialPassword);
    if (!dirty) return draftVersion;
    return save();
  };

  useImperativeHandle(ref, () => ({
    save,
    prepareSubmit,
  }));

  if (loading) return <Spin loading tip="正在加载租户申请" />;
  return <div className="ui-description-panel ui-description-panel--page">
    <div className="ui-description-panel__body">
      <Form form={form} layout="vertical">
        <Form.Item label="租户名称" field="tenantName"
          rules={[{ required: true, message: '请输入租户名称' }, { maxLength: 200 }]}>
          <Input maxLength={200} showWordLimit />
        </Form.Item>
        <Form.Item label="租户类型" field="tenantTypePublicId"
          rules={[{ required: true, message: '请选择租户类型' }]}>
          <Select options={types.map(type => ({
            value: type.tenantTypePublicId, label: type.tenantTypeName,
          }))} />
        </Form.Item>
        <Form.Item label="管理员登录名" field="managerUsername"
          rules={[{ required: true, message: '请输入管理员登录名' }, { maxLength: 100 }]}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item label="管理员姓名" field="managerFullName"
          rules={[{ required: true, message: '请输入管理员姓名' }, { maxLength: 100 }]}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item label="重置初始密码（可选）" field="managerInitialPassword"
          rules={[{ minLength: 8 }, { maxLength: 128 }]}
          extra="留空时保留原密码哈希；密码不会进入流程表单记录。">
          <Input.Password maxLength={128} placeholder="如需重置请输入至少 8 位" />
        </Form.Item>
      </Form>
    </div>
  </div>;
});
